/**
 * Sanitization utilities for ledger entry editor descriptions.
 * Focuses on removing payee/party name suffixes from descriptions.
 */

/**
 * Normalize a party name by stripping non-alphanumeric characters
 * and lowercasing — used for fuzzy matching against description tails.
 */
function normalizePartyName(name: string): string {
  return name.replace(/[^a-z0-9]/gi, '').toLowerCase();
}

/**
 * Remove payee name suffix from description.
 * Handles patterns like "Description to PartyName" where PartyName matches involved_parties.
 *
 * @param description - Original description text
 * @param involvedParties - Array of party objects with name property
 * @returns Sanitized description with payee suffix removed
 *
 * Example:
 *   Input: "Assignment Consideration to Carlos Heriberto Salas Torres"
 *   Parties: [{ name: "Carlos Heriberto Salas Torres" }, ...]
 *   Output: "Assignment Consideration"
 *
 * Safely handles:
 *   - Missing parties array
 *   - Empty strings
 *   - Regex metacharacters in party names
 *   - Whitespace variations
 *   - No match (returns original)
 */
export function sanitizeDescriptionRemovePayee(
  description: string | null | undefined,
  involvedParties?: Array<{ name?: string } | null> | null,
): string {
  // Return empty string for null/undefined
  if (!description) return '';

  // Trim the description
  let trimmed = description.trim();

  // Truncate any payment reference beginning at the first "$" so the backend
  // can add payment information automatically. Preserve text before "$".
  const dollarIndex = trimmed.indexOf('$');
  if (dollarIndex !== -1) {
    trimmed = trimmed.slice(0, dollarIndex).trim();
  }

  const toMatch = /^(.+)\s+([Tt][Oo])\s+(.*)$/.exec(trimmed);
  if (toMatch) {
    const prefix = toMatch[1].trim();
    const tail = toMatch[3];
    const normTail = normalizePartyName(tail);
    const matched = (involvedParties ?? []).some(party => {
      const rawName = party?.name?.trim() ?? '';
      if (!rawName) return false;
      return normalizePartyName(rawName) === normTail;
    });
    if (matched) return prefix;
  }

  // Fallback: strip terminal " to <EntityName>" when no party matched.
  // Requires 2+ words starting with uppercase (entity-like names).
  // Allows lowercase connector words (de, del, of, the) within the entity.
  // Pattern: " to " followed by at least 2 uppercase-starting words, with optional
  // lowercase connectors between them. Anchored at string end to avoid consuming prose.
  const fallbackPattern = /\s+[Tt][Oo]\s+[A-Z][a-zA-Z]*(?:\s+(?:[a-z]+\s+)?[A-Z][a-zA-Z]*)+$/;
  if (fallbackPattern.test(trimmed)) {
    return trimmed.replace(fallbackPattern, '').trim();
  }

  // No match found, return original trimmed description
  return trimmed;
}
